import React, { useRef, useLayoutEffect, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { useTheme } from 'hooks/useTheme';
import useMediaQuery from 'hooks/useMediaQuery';
import { renderWithLineBreaks } from 'utils/renderWithLineBreaks';

gsap.registerPlugin(ScrollTrigger);

const RollingMenu = ({ rollingMenu }) => {
  const isMobileSize = useMediaQuery();
  const { isDarkMode } = useTheme();
  const containerRef = useRef(null);
  const titleContainerRef = useRef(null);
  const staticLeftSideTitleRef = useRef(null);
  const imagesRef = useRef([]);

  useLayoutEffect(() => {
    ScrollTrigger.normalizeScroll(true);
    return () => {
      ScrollTrigger.normalizeScroll(false);
    };
  }, []);

  useGSAP(
    () => {
      let mm = gsap.matchMedia();
      mm.add('(min-width: 1px)', (context) => {
        if (
          !containerRef.current ||
          !titleContainerRef.current ||
          !rollingMenu.data?.length
        ) {
          return;
        }

        const slides = gsap.utils.toArray(
          '.rolling-menu-slide',
          containerRef.current,
        );
        const allTitleWrappers = gsap.utils.toArray(
          '.rolling-menu-title-wrapper',
          titleContainerRef.current,
        );
        const allTitles = gsap.utils.toArray(
          '.rolling-menu-title',
          titleContainerRef.current,
        );
        const allDescriptions = gsap.utils.toArray(
          '.rolling-menu-description',
          containerRef.current,
        );
        imagesRef.current = gsap.utils.toArray(
          '.rolling-menu-image',
          containerRef.current,
        );
        const allLeftSideTitles = gsap.utils.toArray(
          '.rolling-menu-left-title',
          titleContainerRef.current,
        );

        gsap.set(titleContainerRef.current, { yPercent: -50 });

        const titleOffsets = [];
        const titleContainerCenterY =
          titleContainerRef.current.offsetHeight / 2;
        allTitleWrappers.forEach((titleWrapper) => {
          titleOffsets.push(
            titleContainerCenterY -
              (titleWrapper.offsetTop + titleWrapper.offsetHeight / 2),
          );
        });

        gsap.set(titleContainerRef.current, { y: titleOffsets[0] });

        gsap.set(allTitles[0], { opacity: 1 });
        gsap.set(allTitles.slice(1), { opacity: 0.4 });
        gsap.set(allDescriptions, { opacity: 0, yPercent: 20 });
        gsap.set(allDescriptions[0], { opacity: 1, yPercent: 0 });
        gsap.set(slides.slice(1), {
          clipPath: 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)',
        });
        gsap.set(allLeftSideTitles, { opacity: 0, pointerEvents: 'none' });
        if (allLeftSideTitles[0]) {
          gsap.set(allLeftSideTitles[0], { opacity: 1, pointerEvents: 'auto' });
        }

        const masterTimeline = gsap.timeline({
          scrollTrigger: {
            trigger: containerRef.current,
            pin: true,
            pinType: 'transform',
            scrub: 1,
            start: 'top top',
            end: `+=${slides.length * 100}%`,
            invalidateOnRefresh: true,
          },
        });

        slides.forEach((slide, i) => {
          if (i === 0) return;

          const segmentStartTime = i - 1;
          const transitionMidpoint = segmentStartTime + 0.4;

          masterTimeline.to(
            titleContainerRef.current,
            { y: titleOffsets[i], duration: 1, ease: 'power1.inOut' },
            segmentStartTime,
          );
          masterTimeline.to(
            allTitles[i - 1],
            {
              opacity: 0.4,
              fontFamily: `var(--font-primary)`,
              fontStyle: 'normal',
              fontWeight: 400,
              duration: 0.1,
            },
            transitionMidpoint,
          );
          masterTimeline.to(
            allTitles[i],
            {
              opacity: 1,
              fontFamily: `var(--font-secondary)`,
              fontStyle: 'italic',
              fontWeight: 300,
              duration: 0.1,
            },
            transitionMidpoint,
          );
          masterTimeline.to(
            allDescriptions[i - 1],
            { opacity: 0, yPercent: -20, duration: 0.3 },
            transitionMidpoint,
          );
          masterTimeline.to(
            allDescriptions[i],
            { opacity: 1, yPercent: 0, duration: 0.5 },
            transitionMidpoint,
          );
          if (allLeftSideTitles[i - 1]) {
            masterTimeline.to(
              allLeftSideTitles[i - 1],
              { opacity: 0, pointerEvents: 'none', duration: 0.3 },
              transitionMidpoint,
            );
          }
          if (allLeftSideTitles[i]) {
            masterTimeline.to(
              allLeftSideTitles[i],
              { opacity: 1, pointerEvents: 'auto', duration: 0.3 },
              transitionMidpoint,
            );
          }
          masterTimeline.to(
            slide,
            {
              clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
              ease: 'power1.inOut',
              duration: 1,
              force3D: true,
            },
            segmentStartTime,
          );
        });

        return () => {};
      });
    },
    {
      scope: containerRef,
      dependencies: [rollingMenu.data, isMobileSize],
      revertOnUpdate: true,
    },
  );

  useLayoutEffect(() => {
    const images = gsap.utils.toArray(
      '.rolling-menu-image',
      containerRef.current,
    );
    if (images.length === 0) return;

    images.forEach((img, i) => {
      const item = rollingMenu.data[i];
      if (img && item) {
        img.src = isDarkMode
          ? item.darkModeImageUrl || item.lightModeImageUrl
          : item.lightModeImageUrl;
      }
    });
  }, [isDarkMode, rollingMenu.data]);

  const shouldShowDescription = useCallback(
    () => (isMobileSize ? !rollingMenu?.leftSideTitle : true),
    [isMobileSize, rollingMenu?.leftSideTitle],
  );

  return (
    <div
      ref={containerRef}
      className='rolling-menu-container relative overflow-hidden full-pager'
      style={{ minHeight: '100dvh' }}
    >
      {rollingMenu?.leftSideTitle && (
        <p
          ref={staticLeftSideTitleRef}
          className='absolute hp-number-index font-secondary font-white-color uppercase transform-y-50'
          style={{
            left: 'var(--page-padding-x)',
            top: '50%',
            zIndex: 10,
          }}
        >
          {renderWithLineBreaks(rollingMenu?.leftSideTitle)}
        </p>
      )}
      <div
        ref={titleContainerRef}
        className={`absolute full-width flex justify-center flex-column transform-y-50 ${
          rollingMenu?.leftSideTitle ? 'rolling-menu-title-container' : ''
        }`}
        style={{
          ...((!isMobileSize || !rollingMenu?.leftSideTitle) && { top: '50%' }),
          zIndex: 10,
        }}
      >
        {rollingMenu.data.map((item, i) => (
          <div
            key={i}
            className='rolling-menu-title-wrapper flex relative full-width'
          >
            {item.leftSideTitle &&
              !rollingMenu.leftSideTitle &&
              !isMobileSize && (
                <p
                  className='rolling-menu-left-title absolute font-white-color font-secondary uppercase sec-nd-hp-p architektur no-wrap'
                  style={{
                    left: 'var(--page-padding-x)',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 11,
                  }}
                >
                  {renderWithLineBreaks(item.leftSideTitle)}
                </p>
              )}
            <p
              className={`rolling-menu-title font-white-color uppercase sec-nd-hp-p architektur no-wrap ${
                i === 0 ? 'font-secondary' : ''
              } ${rollingMenu?.leftSideTitle ? 'rolling-menu-title-left' : ''}`}
            >
              {renderWithLineBreaks(item.title)}
            </p>
          </div>
        ))}
      </div>
      {rollingMenu.data.map((item, i) => (
        <div
          key={i}
          className='rolling-menu-slide slide absolute'
          style={{
            inset: 0,
            zIndex: i,
          }}
        >
          <img
            className='rolling-menu-image full-height full-width absolute'
            alt={item.title}
            style={{ inset: 0, zIndex: 0 }}
          />
          {shouldShowDescription() && (
            <div
              style={{ zIndex: 1 }}
              className='relative page-wrapper full-height full-width flex'
            >
              <p className='rolling-menu-description paragraph-font-grotesk font-white-color balanced-text margin-top-auto right-0 bottom-0 absolute'>
                {item.description}
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default RollingMenu;
